<template>
  <Teleport to="#appRoot">
    <Transition name="fade">
      <div
        v-show="view.isOpen"
        class="bottom-sheet"
        :class="{
          'bottom-sheet--fullscreen': fullscreen,
        }"
      >
        <div class="bottom-sheet__background" @click="view.close"></div>
        <Transition name="slide-vertical">
          <div
            v-show="view.isOpen"
            ref="elementRef"
            class="bottom-sheet__inner"
            :class="innerClass ? innerClass : ''"
            :style="view.currentPositionCssVariable"
            @touchstart="view.handleTouchStart"
            @touchmove="view.handleTouchMove"
            @touchend="view.handleTouchEnd"
            @touchcancel="view.handleTouchEnd"
          >
            <div class="bottom-sheet__header">
              <button
                v-if="fullscreen"
                class="bottom-sheet__close"
                @click="view.close"
              >
                <span class="iconfont iclose-thin"></span>
              </button>
            </div>

            <slot></slot>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { SheetView } from '@/models/sheet'

/**
 * A modal anchored to the bottom of the screen.
 *
 * This component wraps the given content in a bottom sheet, that can be dismissed
 * by tapping outside of it, or by swiping it down. This component is only visible on
 * tablet and mobile screens.
 */
withDefaults(
  defineProps<{
    innerClass?: string
    fullscreen?: boolean
  }>(),
  {
    fullscreen: false,
  },
)

const elementRef = ref<HTMLElement | null>(null)
const view = ref(new SheetView())

onMounted(() => {
  view.value.setSheetElement(elementRef)
})

defineExpose({
  view,
})
</script>

<style lang="scss" scoped>
.bottom-sheet {
  position: fixed;
  inset: 0;
  z-index: 999;

  @media only screen and (min-width: $w-tablet-b) {
    display: none;
  }

  &__background {
    position: absolute;
    inset: 0;
    background: rgba($c-black-2, 0.3);
  }

  &__inner {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: $c-white;
    padding-inline: 24px;
    padding-block: 40px 28px;
    border-radius: 14px 14px 0 0;
    transform: translateY(var(--sheet-current-position));
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    &:before {
      content: '';
      position: absolute;
      top: 16px;
      left: calc(50% - 40px);
      width: 80px;
      height: 4px;
      border-radius: 4px;
      background: $c-black-13;
    }
  }
}

.bottom-sheet--fullscreen {
  --sheet-top: 32px;
  @supports (padding: env(safe-area-inset-top)) {
    --sheet-top: max(32px, calc(env(safe-area-inset-top) + 16px));
  }

  .bottom-sheet {
    &__inner {
      top: var(--sheet-top);
      padding: 16px;
      padding-top: 0;
      overflow-y: auto;
      overflow-x: hidden;

      &:before {
        display: none;
      }
    }

    &__header {
      position: sticky;
      top: 0;
      width: calc(100% + 32px);
      margin-left: -16px;
      padding: 16px;
      background: $c-white;
      background: linear-gradient(0deg, rgba($c-white, 0), $c-white 16px);
    }

    &__close {
      display: flex;
      width: 28px;
      height: 28px;
      font-size: 28px;
      line-height: 1;
      margin-left: auto;
      color: $c-black-6;
    }
  }
}

.night-mode .bottom-sheet {
  &__background {
    background: rgba($c-black-14, 0.6);
  }

  &__header {
    background: linear-gradient(0deg, rgba($c-dark-2, 0), $c-dark-2 16px);
  }

  &__inner {
    background: $c-dark-2;

    &:before {
      background: $c-dark-5;
    }
  }
}

.slide-vertical {
  @include transition-default();

  &-enter-from,
  &-leave-to {
    transform: translateY(100%);
  }

  &-enter-to,
  &-leave-from {
    transform: translateY(var(--sheet-current-position));
  }
}
</style>
